import api from '@/core/api/modules/media-libraries';
import { makeid } from '@/core/utils/utils';
import VimeoService from './vimeo-service';
import * as tus from 'tus-js-client';
import { Upload } from 'tus-js-client';
import storage from '@/core/store/storage';

function uploadToS3Url(url, file, progressCallback) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', progressCallback);

    xhr.upload.addEventListener('load', () => {
      // wait before file becomes available on S3
      setTimeout(function () {
        resolve(xhr.response);
      }, 2500);
    });
    xhr.upload.addEventListener('error', () => {
      console.log('error');
      reject();
    });

    xhr.open('PUT', url);
    xhr.overrideMimeType(file.type);

    xhr.send(file);
  });
}

function hashCode(str) {
  // from https://stackoverflow.com/a/8831937/151666
  let hash = 0;
  if (str.length === 0) {
    return hash;
  }
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash &= hash; // Convert to 32bit integer
  }
  return hash;
}

function getFileHash(file) {
  return ['tus-br', file.name, file.type, file.size, file.lastModified].join(
    '-'
  );
}

async function upload(
  file,
  onProgressCallback = param => {
    return;
  },
  studioURL = null,
  thumb_width = 0,
  thumb_height = 0
) {
  let apiFile;
  const { type, name, size } = file;
  console.log('type=', type);
  console.log('name=', name);
  console.log('size=', size);
  const fileTypeInfo = type.split('/');

  if (studioURL) {
    const filename = `${new Date().getTime()}-${name}`;
    const { url, public_url } = await api.getS3UploadUrl(filename, type);
    await uploadToS3Url(url, file, ({ loaded, total }) => {
      onProgressCallback(Math.floor((100 * loaded) / total));
    });
    apiFile = await api.uploadStudentMediaFile(
      {
        uploaded_file_file_name: filename,
        uploaded_file_file_type: fileTypeInfo[1],
        uploaded_file_file_size: size,
        uploaded_file_url: public_url,
        uploaded_file_file_content: fileTypeInfo[0],
      },
      studioURL
    );
  } else {
    if (type.includes('image')) {
      apiFile = await api.uploadMediaFile({ file, thumb_width, thumb_height });
    } else if (type.includes('video')) {
      await api.canUploadVideo();
      const fileHash = getFileHash(file);
      console.log('fileHash=', fileHash);
      let uploadInfo = storage.getItem(fileHash);
      if (uploadInfo) {
        console.log(
          'found fileHash in localStorage, resuming upload to ',
          uploadInfo
        );
        uploadInfo = JSON.parse(uploadInfo);
        console.log('cached upload Info', uploadInfo);
      } else {
        uploadInfo = await VimeoService.prepareVimeoUpload(file, '');
        console.log('new upload Info', uploadInfo);
        const cachedUploadInfo = {
          uri: uploadInfo.uri,
          upload: {
            upload_link: uploadInfo.upload.upload_link,
          },
        };
        storage.setItem(fileHash, JSON.stringify(cachedUploadInfo));
      }
      const uploadedInfo: Upload = await VimeoService.tusVimeoUpload(
        file,
        uploadInfo.upload.upload_link,
        onProgressCallback
      );
      console.log('uploaded Info=', uploadedInfo);
      // const uploadInfo = await VimeoService.streamingVimeoUpload(
      //   file,
      //   onProgressCallback
      // );
      //console.log('streaming upload Info=', uploadInfo);
      try {
        apiFile = await api.uploadMediaFile({
          embedded_url: `https://vimeo.com${uploadInfo.uri}`,
          is_uploaded: true,
        });
      } catch (error) {
        throw new Error(error);
      }
      storage.removeItem(fileHash);
    } else {
      const filename = `${new Date().getTime()}-${name}`;
      const { url, public_url } = await api.getS3UploadUrl(filename, type);
      await uploadToS3Url(url, file, ({ loaded, total }) => {
        onProgressCallback(Math.floor((100 * loaded) / total));
      });
      apiFile = await api.uploadMediaFile({
        uploaded_file_file_name: filename,
        uploaded_file_file_type: fileTypeInfo[1],
        uploaded_file_file_size: size,
        uploaded_file_url: public_url,
        uploaded_file_file_content: fileTypeInfo[0],
        is_uploaded: true,
      });
    }
  }

  return apiFile;
}

async function update(file, data) {
  return await api.patchMediaFile(file.id, data);
}

async function uploadPublicUrlImage(
  fileName: string | null = null,
  url: string,
  thumbnailUrl: string | null = null
) {
  if (!fileName) {
    fileName = url.match(/.*\/([^?]+)/)?.[1] || makeid(32);
  }

  const params = {
    uploaded_file_file_name: fileName,
    uploaded_file_file_type: 'jpeg',
    uploaded_file_file_content: 'image',
    embed_image_url: url,
    embed_image: true,
  };
  if (thumbnailUrl) {
    params['embed_thumbnail_url'] = thumbnailUrl;
  }
  const uploadedFile = await api.uploadMediaFile(params);

  return uploadedFile;
}

async function embedFileUrl(url) {
  const file = await api.uploadMediaFile({
    embedded_url: url,
  });

  return file;
}

async function embedFileHtml({ content, contentRaw }) {
  const file = await api.uploadMediaFile({
    content,
    content_raw: contentRaw,
  });

  return file;
}

async function deleteFile(id) {
  return await api.deleteFile(id);
}

async function uploadUnsplashImage(unsplashImage) {
  if (!unsplashImage) return;
  const url = unsplashImage.urls.regular;
  const thumbnailUrl = unsplashImage.urls.thumb;

  let filename = makeid(32);

  try {
    filename = url.match(/.*\/([^?]+)/)[1];
  } catch (error) {
    console.error('Failed to parse filename, using random one');
  }

  const file = await uploadPublicUrlImage(filename, url, thumbnailUrl);

  return file;
}

async function urlToFile(imageUrl, imagePrefix = 'image') {
  const response = await fetch(imageUrl);
  // here image is url/location of image
  const blob = await response.blob();
  // TODO check blob type to see if we get JPG or PNG
  let filename = `${imagePrefix}-${makeid(12)}`;
  try {
    filename = imageUrl.match(/.*\/([^?]+)/)[1];
  } catch (error) {
    console.log('Failed to parse filename, using random one');
  }
  const file = new File([blob], filename, { type: blob.type });
  return file;
}

async function uploadCanvaImageUrl(imageUrl) {
  if (!imageUrl) return;
  const urlFile = await urlToFile(imageUrl, 'canva');

  const file = await upload(urlFile);

  return file;
}

async function uploadVimeoThumbnail(fileId, videoId, file) {
  const videoMeta = await VimeoService.uploadVideoThumbnail(videoId, file);
  const thumbnailUrl = VimeoService.getThumbnailUrl(videoMeta);
  return await api.updateMediaFile(fileId, {
    embedded_thumbnail_url: thumbnailUrl,
    thumbnail_video: thumbnailUrl,
  });
}

async function clearVimeoThumbnail(fileId, videoId) {
  await VimeoService.removeThumbnailActive(videoId);
  return await api.clearFileThumbnails(fileId);
}

async function uploadFileToS3(
  file,
  onProgressCallback = param => {
    return;
  },
  studioURL = null
) {
  const { type, name, size } = file;
  console.log('type=', type);
  console.log('name=', name);
  const filename = `${new Date().getTime()}-${name}`;
  const { url, cdn_url } = await api.getS3UploadUrl(filename, type);

  await uploadToS3Url(url, file, ({ loaded, total }) => {
    onProgressCallback(Math.floor((100 * loaded) / total));
  });
  return cdn_url;
}

// async/promise function for retrieving image dimensions for form file
function imageSize(file) {
  const img = new Image();

  const promise = new Promise((resolve, reject) => {
    img.onload = () => {
      // Natural size is the actual image size regardless of rendering.
      // The 'normal' `width`/`height` are for the **rendered** size.
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      // Resolve promise with the width and height
      resolve({ width, height });
    };

    // Reject promise on error
    img.onerror = reject;
  });
  img.src = window.URL.createObjectURL(file);

  return promise;
}

export const emptyUserFile = {
  id: 'local',
  video_url: null,
  thumbnail: null,
  thumbnail_video: null,
  thumbnail_storage: null,
  storage_url: null,
  file_name: null,
  file_type: null,
  file_content: 'image',
  width: null,
  height: null,
  file_size: null,
  video_length: null,
  uploaded: null,
  locked: false,
  playback_start: 0,
  playback_end: 0,
  embedded_image_url: null,
  embedded_thumbnail_url: null,
  student_uploaded: false,
  alternative: null,
  deletable: true,
};

async function uploadLocal(file) {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  const url = await new Promise(resolve => {
    reader.onload = () => resolve(reader.result);
  });

  const fileName = file.name;
  const fileType = file.type;
  const date = new Date();
  const uploaded = date.toISOString();

  return {
    ...emptyUserFile,
    file_name: fileName,
    file_type: fileType,
    uploaded,
    embedded_image_url: url,
  };
}

async function updateLocal(file, data) {
  return { ...file, ...data };
}

async function localEmbedFileUrl(url) {
  const file = await urlToFile(url);
  const fileName = file.name;
  const fileType = file.type;
  const date = new Date();
  const uploaded = date.toISOString();

  return {
    ...emptyUserFile,
    file_name: fileName,
    file_type: fileType,
    uploaded,
    embedded_image_url: url,
  };
}

async function localUploadUnsplashImage(unsplashImage) {
  if (!unsplashImage) return;
  const url = unsplashImage.urls.regular;

  let filename = makeid(32);

  try {
    filename = url.match(/.*\/([^?]+)/)[1];
  } catch (error) {
    console.error('Failed to parse filename, using random one');
  }

  return {
    ...emptyUserFile,
    file_name: filename,
    file_type: 'jpeg',
    file_content: 'image',
    embedded_image_url: url,
  };
}

export {
  uploadToS3Url,
  upload,
  update,
  embedFileUrl,
  embedFileHtml,
  uploadPublicUrlImage,
  deleteFile,
  uploadUnsplashImage,
  uploadCanvaImageUrl,
  urlToFile,
  uploadVimeoThumbnail,
  clearVimeoThumbnail,
  uploadFileToS3,
  imageSize,
  uploadLocal,
  updateLocal,
  localEmbedFileUrl,
  localUploadUnsplashImage,
};
